import { TGoogleRoutePoint } from "../../../api/types/Map";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { APP_CONFIG } from "../../../constants/constants";
import {
  getGoogleRoutePointsFromTripPoints,
  gSnapPointsToRoad,
  renderRouteWithPolylines,
} from "./helper";
import { RenderRoute } from "./renderRoute.class";

export class RenderRouteSnapToRoad extends RenderRoute {
  // static method to get the computed route points by snapping the original trip points to road using google snap to road API,
  // which can be used independently wherever we need to get the snapped points for a given set of trip points, without needing to create an instance of the class and calling
  static getComputedRoutePoints = async ({
    tripIndex,
    sanitizedTripPoints,
    refString = "",
  }: {
    tripIndex: number;
    sanitizedTripPoints: VehicleMapDataPoint[];
    refString?: string; // optional string to identify the caller in debug logs
  }): Promise<TGoogleRoutePoint[]> => {
    // snap the trip points to road using google snap to road API and get the interpolated points in the response to render the route using those points.
    //
    // Fails soft: a hard Snap-to-Road failure (network, quota, malformed
    // response) resolves to an empty array rather than rejecting. The render
    // orchestrator owns the fallback decision (step down to another method,
    // ultimately to the raw-GPS straight line), so a failure here must never
    // propagate up and tear down the whole trip's render. This is what the
    // single-day path previously lacked - a Snap rejection used to throw out
    // of the segment render entirely.
    return new Promise((resolve) => {
      gSnapPointsToRoad({
        tripIndex,
        tripPoints: sanitizedTripPoints,
        successCallback: ({ pointsOriginal, pointsInterpolated }) => {
          if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD)
            console.log(
              `[RNDRT:${refString ? `${refString}→` : ""}STR] T#${tripIndex} RES orig=${pointsOriginal.length} interp=${pointsInterpolated.length}`,
              { pointsOriginal, pointsInterpolated }
            );
          resolve(pointsInterpolated);
        },
        errorCallback: (points) => {
          if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD)
            console.warn(
              `[RNDRT:${refString ? `${refString}→` : ""}STR] T#${tripIndex} ERR - resolving empty so caller can fall back`,
              points
            );
          resolve([]);
        },
      });
    });
  };

  preRender = async () => {
    const { tripIndex, sanitizedTripPoints } = this;
    // snap the trip points to road using google snap to road API and get the interpolated points in the response to render the route using those points
    return await RenderRouteSnapToRoad.getComputedRoutePoints({
      tripIndex,
      sanitizedTripPoints,
    });
  };

  render = async () => {
    const interpolatedTripPoints =
      (await this.preRender()) as TGoogleRoutePoint[];
    // if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD) { console.log("XXinterpolatedTripPoints trip#", this.tripIndex, interpolatedTripPoints, "original sanitizedTripPoints", this.sanitizedTripPoints, "polylineConfig", this.polylineConfig); }

    const { props, tripIndex, sanitizedTripPoints, polylineConfig } = this;
    const routeVisibility = props.mapRef.current.routeViewsEnabled.SNAP_TO_ROAD;

    const gRoutePoints: TGoogleRoutePoint[] =
      getGoogleRoutePointsFromTripPoints({ tripPoints: sanitizedTripPoints });

    // render the route
    if (
      props?.selectedTripIndex !== null &&
      tripIndex !== props?.selectedTripIndex
    ) {
      // do nothing
    } else {
      // renderRouteWithPolylines();
      const mapElements = renderRouteWithPolylines({
        props,
        tripIndex,
        polylineConfig,
        gRoutePoints:
          !!interpolatedTripPoints && interpolatedTripPoints.length > 0
            ? interpolatedTripPoints.map((point) => ({
                ...point,
                location: new google.maps.LatLng(
                  point.location.latitude,
                  point.location.longitude
                ) as any, // strangely typescript is giving error if I directly typecast the location as google.maps.LatLng, so added 'as any'
              }))
            : gRoutePoints,
        routeVisibility,
      });
      this.setMapElements(mapElements);
    }

    // set route visibility in mapRef
    // 1 for visible, 0 for hidden. Initially setting the visibility based on routeViewsEnabled,
    // and then it can be toggled using show/hide methods of the class which will also update this visibility value accordingly
    props.mapRef.current.objects.mTripsVisibility.push(routeVisibility ? 1 : 0);

    this.isInitialized = true; // set initialized flag to true after first render
  };
}
